import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import DeleteSVG from "../../assets/common/DeleteSVG";
import VectorSVG from "../../assets/common/VectorSVG";
import ToggleOffSVG from "../../assets/service/ToggleOffSVG";
import ToggleOnSVG from "../../assets/service/ToggleOnSVG";
import IconSVG from "../../assets/teamMember/IconSVG";
import { getAllCategories } from "../../network/category";
import {
  addMembership,
  showMembership,
  updateMembership,
} from "../../network/membership";
import { listParentwithServices } from "../../network/service";
import { queryKeys } from "../../services/react-query/queryKeys";
import { Category } from "../../types/category";
import { service } from "../../types/service";
import { checkStringInLanguage } from "../../utils/utils";
import SubInfo from "../admin/common/SubInfo";
import GenderSwitch from "../common/GenderSwitch";
import RadioButtonWithSVG from "../common/ui/RadioButtonWithSVG";

interface AddMembershipFormProps {
  visible: boolean;
  onClose: () => void;
  editMembershipData: any;
}

const AddMembershipForm: React.FC<AddMembershipFormProps> = ({
  visible,
  onClose,
  editMembershipData,
}) => {
  const { locationId, currentLang } = useSelector(
    (state: {
      calendar: { locationId: number };
      user: { currentLang: "ar" | "en" };
    }) => ({
      locationId: state.calendar.locationId,
      currentLang: state.user.currentLang,
    })
  );
  const { data: membershipDetails, isLoading: isMembershipLoading } = useQuery(
    ["membershipDetails", editMembershipData?.id],
    () => showMembership(locationId, editMembershipData?.id),
    {
      enabled: !!editMembershipData?.id && !!locationId,
      select: (res) => res?.data,
    }
  );
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [selectedOption, setSelectedOption] = useState("limited");
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [selectedServices, setSelectedServices] = useState<
    Array<{
      service_id: number;
      pricing_option_id: number;
      name: string;
      price: string;
      duration: string;
    }>
  >([]);

  const handleOptionSelect = (option: string) => {
    setSelectedOption(option);
  };

  const { data: listCategories } = useQuery([queryKeys.listCategories], () =>
    getAllCategories({ "location_ids[0]": locationId })
  );

  const { data: listServices, isLoading: isServiceLoading } = useQuery(
    [queryKeys.listServices, selectedCategoryId],
    () =>
      listParentwithServices({
        category_id: selectedCategoryId,
        location_id: locationId,
      }),
    {
      enabled: !!selectedCategoryId,
    }
  );

  useEffect(() => {
    if (membershipDetails) {
      const initialValues = {
        name: membershipDetails?.name,
        available_for: membershipDetails?.available_for,
        description: membershipDetails?.description,
        valid_id: membershipDetails?.voucher_duration?.id,
        expire_duration: membershipDetails?.expire_duration,
        terms_and_conditions: membershipDetails?.terms_and_conditions,
        price: membershipDetails?.price,

        number_sessions: membershipDetails?.number_sessions,
        services: [{}],
      };

      form.setFieldsValue(initialValues);
      if (membershipDetails?.pricing_option?.length) {
        const initialServices = membershipDetails?.pricing_option?.map(
          (pricingOption: {
            id: number;
            name: { ar: string; en: string };
            service_id: number;
            service: { id: number; name: { ar: string; en: string } };
            price: string;
            duration: string;
          }) => ({
            service_id: pricingOption?.service_id,
            pricing_option_id: pricingOption?.id,
            name: pricingOption?.service?.name[currentLang],
            price: pricingOption?.price,
            duration: pricingOption?.duration,
          })
        );
        const sessions = membershipDetails?.sessions;
        console.log(initialServices);
        // Set the initial values in the state
        setSelectedOption(sessions);
        setSelectedServices(initialServices);
      }
    }
  }, [membershipDetails, editMembershipData?.id]);

  const handleRemoveService = (pricingOptionId: number) => {
    const newServices = selectedServices?.filter(
      (service) => service?.pricing_option_id !== pricingOptionId
    );

    setSelectedServices(newServices);
  };

  // const { data: listVoucherDurations } = useQuery(
  //   [queryKeys.listVoucherDurations],
  //   () => getVoucherDuration()
  // );

  const expireDuration = [
    {
      value: "one_month",
      label: "One Month",
    },
    {
      value: "three_months",
      label: "Three Months",
    },
    {
      value: "six_months",
      label: "Six Months",
    },
    {
      value: "one_year",
      label: "One Year",
    },
  ];

  const { mutate: onAddMemberships, isLoading } = useMutation(
    (data: any) => addMembership(locationId, data),
    {
      onSuccess: (res: any) => {
        if (res?.response?.status === 422 || res?.response?.status === 500) {
          message.error(res?.response?.data?.message);
        } else {
          message.success("Membership created successfully");
          queryClient.invalidateQueries([queryKeys.listMemberships]);
          onClose();
        }
      },
      onError: (error) => {
        message.error("حاول مرة اخرى");
      },
    }
  );
  const { mutate: onEditMemberships, isLoading: updateLoading } = useMutation(
    (params) => updateMembership(locationId, editMembershipData.id, params),
    {
      onSuccess: (res: any) => {
        if (res?.code !== "ERR_BAD_REQUEST") {
          message.success("Submit success!");
          queryClient.invalidateQueries([queryKeys.listMemberships]);
          queryClient.invalidateQueries([
            "membershipDetails",
            editMembershipData.id,
          ]);
          onClose();
        } else {
          message.error(res?.response?.data?.message);
        }
      },
      onError: (error) => {
        message.error("حاول مرة اخرى");
      },
    }
  );

  const onFinish = async (values: any) => {
    values.services = selectedServices;
    values.sessions = selectedOption;
    values.enable_online_booking = true;
    values.description["ar"] = values?.description?.ar;
    console.log(values);
    if (editMembershipData) {
      onEditMemberships(values);
    } else {
      onAddMemberships(values);
    }
  };

  const [active, setActive] = useState("both");

  const changeGender = (e: any) => {
    setActive(e.target.value);
  };
  const intl = useIntl();

  return (
    <Modal
      centered
      destroyOnClose
      open={visible}
      onCancel={onClose}
      okButtonProps={{ disabled: true }}
      cancelButtonProps={{ disabled: true }}
      footer={[]}
      width={1100}
    >
      {editMembershipData?.id && isMembershipLoading ? (
        <div className="flex items-center justify-center ">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div className="text-2xl font-semibold leading-7 text-center text-color-primary">
            {intl.formatMessage({
              id: editMembershipData?.id
                ? "edit_membership_title"
                : "add_membership_title",
            })}
          </div>
          <div className="my-4 text-base font-normal leading-7 text-center text-color-gray">
            {intl.formatMessage({ id: "add_membership_description" })}
          </div>
          <Form
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              services: [{}],
            }}
            form={form}
          >
            <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14 }}>
              <Col
                className="gutter-row"
                xl={12}
                lg={12}
                md={12}
                xs={24}
                sm={8}
                span={11}
              >
                <Form.Item
                  name={["name", "ar"]}
                  label={
                    intl.formatMessage({ id: "membership_name_label" }) +
                    " " +
                    intl.formatMessage({ id: "inArabic" })
                  }
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "membership_name_required_message",
                      }),
                    },
                    {
                      type: "string",
                      min: 2,
                      message: intl.formatMessage({
                        id: "membership_name_min_length_message",
                      }),
                    },
                    {
                      validator: (_, value) => {
                        if (value) {
                          if (checkStringInLanguage(value, "ar")) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(
                              new Error(
                                intl.formatMessage({
                                  id: "service_name_arabic_error",
                                })
                              )
                            );
                          }
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder={
                      intl.formatMessage({ id: "membership_name_label" }) +
                      " " +
                      intl.formatMessage({ id: "inArabic" })
                    }
                  />
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                xl={12}
                lg={12}
                md={12}
                xs={24}
                sm={8}
                span={11}
              >
                <Form.Item
                  name={["name", "en"]}
                  label={
                    intl.formatMessage({ id: "membership_name_label" }) +
                    " " +
                    intl.formatMessage({ id: "inEnglish" })
                  }
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "membership_name_required_message",
                      }),
                    },
                    {
                      type: "string",
                      min: 2,
                      message: intl.formatMessage({
                        id: "membership_name_min_length_message",
                      }),
                    },
                    {
                      validator: (_, value) => {
                        if (value) {
                          if (checkStringInLanguage(value, "en")) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(
                              new Error(
                                intl.formatMessage({
                                  id: "service_name_english_error",
                                })
                              )
                            );
                          }
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder={
                      intl.formatMessage({ id: "membership_name_label" }) +
                      " " +
                      intl.formatMessage({ id: "inEnglish" })
                    }
                  />
                </Form.Item>
              </Col>
              <Col
                className="gutter-row"
                xl={12}
                lg={12}
                md={12}
                xs={24}
                sm={8}
                span={11}
              >
                <Form.Item
                  label={
                    <Flex>
                      <span className="mx-1">
                        {intl.formatMessage({
                          id: "membership_description_label",
                        })}
                      </span>
                      <small className="text-[12px] text-normal self-center mt-2 text-[#75757E]">
                        (
                        {intl.formatMessage({
                          id: "membership_description_optional_note",
                        })}
                        )
                      </small>
                    </Flex>
                  }
                  name={["description", "ar"]}
                  required={false}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "membership_description_placeholder",
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>
            <div className="flex items-center justify-between">
              <div className="w-full mx-1 text-lg font-normal">
                {intl.formatMessage({ id: "available_for" })}
              </div>
              <Form.Item
                name="available_for"
                required={false}
                rules={[{ required: true }]}
                initialValue={active}
                className="!mb-4"
              >
                <Radio.Group
                  onChange={changeGender}
                  defaultValue={membershipDetails?.available_for || "both"}
                  className="flex items-center gap-4 my-auto"
                >
                  <GenderSwitch
                    active={"woman" === active}
                    gender="woman"
                    string={intl.formatMessage({ id: "woman" })}
                  />

                  <GenderSwitch
                    active={"man" === active}
                    gender="man"
                    string={intl.formatMessage({ id: "man" })}
                  />

                  <GenderSwitch
                    active={"both" === active}
                    gender="both"
                    string={intl.formatMessage({ id: "both" })}
                  />
                </Radio.Group>
              </Form.Item>
            </div>

            <div className="w-full px-2 pt-2 mb-5 rounded bg-color-light-gr text-color-primary">
              <Form.List name="services">
                {(fields, { add, remove }) => (
                  <>
                    <Form.Item className="w-full p-2 rounded bg-color-light-gr text-color-primary">
                      <div>
                        <Row className="mb-4">
                          <Col
                            className="gutter-row"
                            xl={24}
                            lg={8}
                            xs={24}
                            sm={24}
                            span={11}
                          >
                            <label className="text-color-primary">
                              {intl.formatMessage({
                                id: "servicesLabel",
                              })}
                            </label>
                          </Col>
                        </Row>

                        {fields.map(({ key, name, ...restField }, index) => (
                          <Row
                            key={key}
                            gutter={{ xs: 8, sm: 16, md: 24, lg: 15 }}
                          >
                            <Col
                              className="gutter-row"
                              xl={11}
                              lg={12}
                              xs={24}
                              sm={12}
                              span={12}
                            >
                              <Form.Item
                                {...restField}
                                required={false}
                                label={intl.formatMessage({
                                  id: "service_category_label",
                                })}
                                rules={[
                                  {
                                    required: true,
                                    message: intl.formatMessage({
                                      id: "service_category_required_message",
                                    }),
                                  },
                                ]}
                              >
                                <Select
                                  placeholder={intl.formatMessage({
                                    id: "service_category_placeholder",
                                  })}
                                  className="w-full my-input"
                                  options={listCategories?.data?.map(
                                    (item: Category) => ({
                                      value: item?.id,
                                      label: item?.name,
                                    })
                                  )}
                                  onChange={(value) => {
                                    console.log(value);
                                    setSelectedCategoryId(+value);
                                  }}
                                />
                              </Form.Item>
                            </Col>

                            <Col
                              className="gutter-row"
                              xl={11}
                              lg={12}
                              xs={24}
                              sm={12}
                              span={12}
                            >
                              <Form.Item
                                {...restField}
                                required={false}
                                name={["services"]}
                                label={intl.formatMessage({
                                  id: "service_label",
                                })}
                              >
                                <Select
                                  placeholder={intl.formatMessage({
                                    id: "select_service_placeholder",
                                  })}
                                  loading={isServiceLoading}
                                  disabled={!!isServiceLoading}
                                  className="w-full my-input"
                                  options={
                                    listServices?.data
                                      ? listServices?.data?.flatMap(
                                          (service: any) => {
                                            return service?.pricing_options?.map(
                                              (pricingOption: any) => {
                                                console.log(
                                                  pricingOption,
                                                  "CC ....."
                                                );
                                                const selectedBefore =
                                                  selectedServices?.find(
                                                    (item: {
                                                      service_id: number;
                                                      pricing_option_id: number;
                                                    }) =>
                                                      item?.service_id ===
                                                        service?.id &&
                                                      item?.pricing_option_id ===
                                                        pricingOption?.id
                                                  );
                                                if (selectedBefore) {
                                                  return {
                                                    value: `${service?.id}-${pricingOption?.id}`,
                                                    label: `${service?.name} - ${pricingOption?.price} ريال سعودي`,
                                                    disabled: true,
                                                  };
                                                }
                                                return {
                                                  value: `${service?.id}-${pricingOption?.id}`,
                                                  label: (
                                                    <p className="flex gap-2 ">
                                                      <span className="capitalize">
                                                        {service?.name}
                                                      </span>
                                                      <SubInfo
                                                        value={
                                                          pricingOption?.price
                                                        }
                                                        label={intl.formatMessage(
                                                          {
                                                            id: "SARshort",
                                                          }
                                                        )}
                                                      />
                                                    </p>
                                                  ),
                                                };
                                              }
                                            );
                                          }
                                        )
                                      : []
                                  }
                                />
                              </Form.Item>
                            </Col>
                            <Col
                              className="gutter-row"
                              xl={1}
                              lg={12}
                              xs={24}
                              sm={12}
                              span={12}
                            >
                              <Form.Item label=" ">
                                <Button
                                  className="bg-transparent border-none shadow-none outline-none"
                                  onClick={() => {
                                    const selectedServiceId =
                                      form.getFieldValue(["services"])[
                                        "services"
                                      ];
                                    if (typeof selectedServiceId !== "string") {
                                      return false;
                                    }
                                    const [serviceId, pricingOptionId] =
                                      selectedServiceId?.split("-");
                                    const service: service | undefined =
                                      listServices?.data?.find(
                                        (service: service) =>
                                          service?.id === parseInt(serviceId)
                                      );
                                    if (!service) return;
                                    const pricingOption =
                                      service?.pricing_options?.find(
                                        (pricingOption) =>
                                          pricingOption?.id ===
                                          parseInt(pricingOptionId)
                                      );
                                    if (!pricingOption) return;
                                    const selectedOptionBefore =
                                      selectedServices.find(
                                        (item: {
                                          service_id: number;
                                          pricing_option_id: number;
                                        }) =>
                                          item.service_id ===
                                            parseInt(serviceId) &&
                                          item.pricing_option_id ===
                                            parseInt(pricingOptionId)
                                      );
                                    if (selectedOptionBefore) return false;
                                    setSelectedServices([
                                      ...selectedServices,
                                      {
                                        service_id: parseInt(serviceId),
                                        pricing_option_id:
                                          parseInt(pricingOptionId),
                                        price: pricingOption?.price,
                                        name: service?.name,
                                        duration: pricingOption?.duration,
                                      },
                                    ]);
                                    form.resetFields(["services", index]);
                                  }}
                                  block
                                  icon={
                                    <PlusOutlined className="bg-[#00086D] text-white p-1 rounded-md" />
                                  }
                                ></Button>
                              </Form.Item>
                            </Col>
                          </Row>
                        ))}
                        <div>
                          {selectedServices.map((pricingOption) => (
                            <>
                              <div
                                key={pricingOption?.pricing_option_id}
                                className="flex justify-between gap-2 p-1 px-2 my-1 bg-white rounded"
                              >
                                <div className="flex items-center ">
                                  <div className="flex items-center gap-1">
                                    <span>
                                      <IconSVG />
                                    </span>
                                    <span>{pricingOption?.name}</span>
                                  </div>
                                  <div className="mx-4">
                                    <VectorSVG />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span>{pricingOption?.duration}</span>
                                    <small className="self-center mt-2 text-xs">
                                      {intl.formatMessage({ id: "minutes" })}
                                    </small>
                                  </div>
                                  <div className="mx-4">
                                    <VectorSVG />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <span>{pricingOption?.price}</span>
                                    <small className="self-center mt-2 text-xs">
                                      {intl.formatMessage({
                                        id: "SAR",
                                      })}
                                    </small>
                                  </div>
                                </div>

                                <Button
                                  type="text"
                                  htmlType="button"
                                  onClick={() =>
                                    handleRemoveService(
                                      pricingOption?.pricing_option_id
                                    )
                                  }
                                >
                                  <DeleteSVG />
                                </Button>
                              </div>
                            </>
                          ))}
                        </div>
                      </div>
                    </Form.Item>
                  </>
                )}
              </Form.List>

              <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-center">
                  <label className="w-full text-base text-xl font-normal text-right text-black ">
                    {intl.formatMessage({ id: "session_count" })}
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <div className="">
                    <Form.Item name="sessions">
                      <Row
                        gutter={{ xs: 8, sm: 0, md: 24, lg: 14 }}
                        justify={"space-between"}
                      >
                        <Col className="gutter-row">
                          <RadioButtonWithSVG
                            option={intl.formatMessage({
                              id: "unlimited_sessions",
                            })}
                            offSVG={<ToggleOffSVG />}
                            className="h-[42px] p-2 mb-2 bg-white"
                            onSVG={<ToggleOnSVG />}
                            value="unlimited"
                            selectedOption={selectedOption}
                            onSelect={handleOptionSelect}
                          />
                        </Col>
                        <Col className="gutter-row">
                          <RadioButtonWithSVG
                            option={intl.formatMessage({
                              id: "limited_sessions",
                            })}
                            className="h-[42px] p-2 mb-2 bg-white"
                            value="limited"
                            offSVG={<ToggleOffSVG />}
                            onSVG={<ToggleOnSVG />}
                            selectedOption={selectedOption}
                            onSelect={handleOptionSelect}
                          />
                        </Col>
                      </Row>
                    </Form.Item>
                  </div>
                  <div>
                    {selectedOption === "limited" && (
                      <Form.Item
                        name={"number_sessions"}
                        required={false}
                        rules={[
                          {
                            required: true,
                            message: intl.formatMessage({
                              id: "enter_session_count",
                            }),
                          },
                          {
                            validator(rule, value, callback) {
                              if (value <= 0) {
                                return Promise.reject(
                                  intl.formatMessage({
                                    id: "session_count_validation",
                                  })
                                );
                              } else {
                                return Promise.resolve();
                              }
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder={intl.formatMessage({
                            id: "select_session_count",
                          })}
                          className="w-full my-input"
                          type="number"
                          min={1}
                        />
                      </Form.Item>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Form.Item className="w-full p-2 rounded bg-color-light-gr text-color-primary">
              <Row className="mb-4">
                <Col
                  className="gutter-row"
                  xl={24}
                  lg={8}
                  xs={24}
                  sm={24}
                  span={11}
                >
                  <label className="text-color-primary">
                    {intl.formatMessage({ id: "price" })}
                  </label>
                </Col>
              </Row>

              <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14 }}>
                <Col
                  className="gutter-row"
                  xl={12}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name={"price"}
                    label={intl.formatMessage({ id: "price" })}
                    required={false}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "membership_price_required",
                        }),
                      },
                      {
                        validator(rule, value, callback) {
                          if (value <= 0) {
                            return Promise.reject(
                              intl.formatMessage({
                                id: "membership_price_validation",
                              })
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      min={1}
                      placeholder={intl.formatMessage({
                        id: "membership_price_placeholder",
                      })}
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xl={12}
                  lg={12}
                  md={12}
                  xs={24}
                  sm={8}
                  span={11}
                >
                  <Form.Item
                    name={"expire_duration"}
                    label={intl.formatMessage({ id: "membership_validity" })}
                    required={false}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "membership_validity_required",
                        }),
                      },
                    ]}
                  >
                    <Select
                      placeholder={intl.formatMessage({
                        id: "choose_membership_validity",
                      })}
                      id="input-with-icon-adornment"
                      options={expireDuration || []}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>

            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 14 }} className="mt-5">
              <Col
                className="gutter-row"
                xl={24}
                lg={12}
                xs={24}
                sm={8}
                span={11}
              >
                <Form.Item
                  name={"terms_and_conditions"}
                  label={
                    <Flex align="center">
                      <span className="mx-1">
                        {intl.formatMessage({ id: "terms_and_conditions" })}
                      </span>
                      <span className="text-normal text-sm text-[#75757E]">
                        ({intl.formatMessage({ id: "optional" })})
                      </span>
                    </Flex>
                  }
                  className="w-full"
                >
                  <Input.TextArea
                    id="outlined-basic"
                    rows={2}
                    size="large"
                    placeholder={intl.formatMessage({
                      id: "terms_and_conditions_placeholder",
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Flex gap="small" align="center" justify="center">
              <Button
                type="primary"
                loading={isLoading || updateLoading}
                htmlType="submit"
                block
              >
                {intl.formatMessage({
                  id: editMembershipData?.id ? "save" : "add",
                })}
              </Button>
              <Button
                type="primary"
                onClick={onClose}
                disabled={isLoading}
                block
                className="btn-primary-light"
              >
                {intl.formatMessage({ id: "cancel" })}
              </Button>
            </Flex>
          </Form>
        </>
      )}
    </Modal>
  );
};

export default AddMembershipForm;
